<template>
    <div class="business-listing">
        <!-- Loading State (Initial) -->
        <div v-if="loading && businesses.length === 0" class="text-center py-5">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading businesses...</p>
        </div>

        <!-- Business Slider -->
        <div v-if="businesses.length > 0" class="business-slider-wrapper">

            <!-- Status Bar -->
            <div class="slider-status-bar mb-3">
                <div class="status-info">
                    <span class="badge bg-primary">{{ currentSlide + 1 }} / {{ total }}</span>
                    <span class="text-muted ms-2">
                        Showing {{ businesses.length }} of {{ total }} businesses
                    </span>
                </div>
                <div class="status-controls">
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        @click="togglePause"
                        :title="isPaused ? 'Resume auto-slide' : 'Pause auto-slide'"
                    >
                        <i :class="isPaused ? 'fas fa-play' : 'fas fa-pause'"></i>
                        {{ isPaused ? 'Resume' : 'Pause' }}
                    </button>
                </div>
            </div>

            <!-- Slider Container -->
            <div class="business-slider-container" ref="sliderContainer">
                <div
                    class="business-slider-track"
                    :style="{
                        transform: `translateX(-${offset}px)`,
                        gap: gap + 'px'
                    }"
                    :class="{ 'no-transition': !animate }"
                >
                    <!-- Business Cards -->
                    <div
                        v-for="(business, index) in businesses"
                        :key="business.id || index"
                        class="business-slide-item"
                        :style="{
                            width: calculatedCardWidth + 'px',
                            minWidth: calculatedCardWidth + 'px'
                        }"
                    >
                        <div class="card aos aos-init aos-animate">
                            <div class="blog-widget">
                                <div class="blog-img">
                                    <a :href="'/business/' + business.location?.city_id + '/' + business.slug">
                                        <img
                                            v-if="business.featured_banner && business.featured_banner.image"
                                            :src="business.featured_banner.image"
                                            class="img-fluid"
                                            :alt="business.featured_banner.title"
                                            @error="handleImageError"
                                            loading="lazy"
                                        >
                                        <div v-else class="banner-placeholder">
                                            <div class="placeholder-content">
                                                <i class="fa-regular fa-image"></i>
                                                <span>{{ business.business_name || 'MYBN' }}</span>
                                            </div>
                                        </div>
                                    </a>
                                    <div class="fav-item">
                                        <span class="featured-text" v-if="business.is_featured">Featured</span>
                                        <a href="javascript:void(0)" class="fav-icon">
                                            <i class="fa-regular fa-heart"></i>
                                        </a>
                                    </div>
                                </div>
                                <div class="bloglist-content">
                                    <div class="card-body">
                                        <div class="blogfeaturelink">
                                            <div class="grid-author">
                                                <img
                                                    v-if="business.profilepic"
                                                    :src="business.profilepic"
                                                    :alt="business.business_name"
                                                    @error="handleImageError"
                                                    loading="lazy"
                                                >
                                            </div>
                                            <div class="blog-features">
                                                <a href="javascript:void(0)">
                                                    <span>
                                                        <i class="fa-regular fa-circle-stop"></i>
                                                        {{ business.managed_by }}
                                                    </span>
                                                </a>
                                            </div>
                                            <div class="blog-author text-end">
                                                <span>
                                                    <i class="fa-regular fa-eye"></i>
                                                    {{ business.stats?.total_views || 0 }}
                                                </span>
                                            </div>
                                        </div>
                                        <h3>
                                            <a :href="'/business/' + business.location?.city_id + '/' + business.slug">
                                                {{ business.business_name }}
                                            </a>
                                        </h3>
                                        <div class="blog-location-details">
                                            <div class="location-info">
                                                <i class="fa fa-map-marker"></i>
                                                {{ business.location?.city_name || business.city }},
                                                {{ business.location?.state_name || business.state }}
                                            </div>
                                            <div class="location-info">
                                                <i class="fa-solid fa-calendar-days"></i>
                                                {{ $formatDate(business.updated_at, 'displayDate') }}
                                            </div>
                                        </div>
                                        <div class="business-description mt-2">
                                            <p class="text-muted">{{ truncate(business.description, 100) }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Loading More Indicator Inside Track -->
                    <div
                        v-if="loadingMore"
                        class="business-slide-item loading-slide-item"
                        :style="{
                            width: calculatedCardWidth + 'px',
                            minWidth: calculatedCardWidth + 'px'
                        }"
                    >
                        <div class="loading-card">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="mt-2 text-muted">Loading more...</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Slider Controls -->
            <div class="slider-controls mt-3">
                <button
                    class="slider-nav-btn"
                    @click="prevSlide"
                    :disabled="businesses.length <= cardsVisible"
                    title="Previous"
                >
                    <i class="fas fa-chevron-left"></i>
                </button>

                <button
                    class="slider-nav-btn"
                    @click="nextSlide"
                    :disabled="businesses.length <= cardsVisible"
                    title="Next"
                >
                    <i class="fas fa-chevron-right"></i>
                </button>

                <button
                    class="slider-nav-btn restart-btn"
                    @click="restart"
                    title="Restart from beginning"
                >
                    <i class="fas fa-redo"></i>
                </button>
            </div>

            <!-- Progress Bar -->
            <div class="slider-progress mt-3">
                <div class="progress-track">
                    <div
                        class="progress-bar-fill"
                        :style="{ width: progressPercentage + '%' }"
                    ></div>
                </div>
                <div class="progress-labels">
                    <small class="text-muted">
                        Slide {{ currentSlide + 1 }} of {{ total }}
                    </small>
                    <small class="text-muted">
                        {{ Math.round(progressPercentage) }}% complete
                    </small>
                </div>
            </div>
        </div>

        <!-- No Results -->
        <div v-if="!loading && businesses.length === 0" class="text-center py-5">
            <div class="empty-state">
                <i class="fas fa-store-slash fa-3x text-muted mb-3"></i>
                <h4>No businesses found</h4>
                <a :href="'/business'">Back</a>
            </div>
        </div>
    </div>
</template>

<script>
import ApiService from '../services/ApiService';

export default {
    name: 'GothBusinessList',

    props: {
        perPage: {
            type: Number,
            default: 20
        },
        totalLimit: {
            type: Number,
            default: 250
        },
        slideInterval: {
            type: Number,
            default: 20000 // 20 seconds (each card visible for 40s with 2 visible)
        },
        cardsToSlide: {
            type: Number,
            default: 1
        },
        cardsVisible: {
            type: Number,
            default: 2 // 2 cards fully visible
        }
    },

    data() {
        return {
            businesses: [],
            search: '',
            sortBy: 'created_at',
            sortOrder: 'desc',
            loading: false,
            loadingMore: false,
            currentPage: 1,
            lastPage: 1,
            total: 0,
            hasMoreData: true,
            searchTimeout: null,
            resizeObserver: null,

            // ===== Slider State =====
            currentSlide: 0,
            offset: 0,
            isPaused: false,
            animate: true,
            slideTimer: null,
            loadMoreThreshold: 3,

            // ===== Dynamic Dimensions =====
            containerWidth: 0,
            gap: 20, // gap between cards in px
        };
    },

    computed: {
        // Calculate exact width so N cards fit perfectly with no partial
        calculatedCardWidth() {
            if (this.containerWidth === 0) return 350;

            const totalGaps = (this.cardsVisible - 1) * this.gap;
            const width = (this.containerWidth - totalGaps) / this.cardsVisible;

            // Safety: don't go below 200px
            return Math.max(200, Math.floor(width));
        },

        progressPercentage() {
            if (this.total === 0) return 0;
            return ((this.currentSlide + 1) / this.total) * 100;
        },

        maxSlide() {
            return Math.max(0, this.total - this.cardsVisible);
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.measureContainer();
        });

        // Watch container resize (more reliable than window resize)
        if (typeof ResizeObserver !== 'undefined') {
            this.$nextTick(() => {
                const container = this.$refs.sliderContainer;
                if (container) {
                    this.resizeObserver = new ResizeObserver(() => {
                        this.measureContainer();
                        this.$nextTick(() => this.updateOffset());
                    });
                    this.resizeObserver.observe(container);
                }
            });
        }

        window.addEventListener('resize', this.handleResize);
        this.fetchBusinesses();
    },

    beforeUnmount() {
        this.stopAutoSlide();
        clearTimeout(this.searchTimeout);
        window.removeEventListener('resize', this.handleResize);

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },

    methods: {
        // ==================== API METHODS ====================

        async fetchBusinesses(reset = true) {
            if (reset) {
                this.loading = true;
                this.currentPage = 1;
                this.hasMoreData = true;
            } else {
                this.loadingMore = true;
            }

            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.search,
                    sort_by: this.sortBy,
                    sort_order: this.sortOrder
                };

                const response = await ApiService.businesses.getAll(params);

                if (reset) {
                    this.businesses = response.data.data || [];
                } else {
                    this.businesses = [...this.businesses, ...(response.data.data || [])];
                }

                const pagination = response.data.pagination;
                this.currentPage = pagination.current_page;
                this.lastPage = pagination.last_page;
                this.total = Math.min(pagination.total, this.totalLimit);
                this.hasMoreData = this.currentPage < this.lastPage && this.businesses.length < this.totalLimit;

                if (reset) {
                    this.updateUrl(params);
                }

                // Start auto-slide after first load
                if (reset && this.businesses.length > 0) {
                    this.$nextTick(() => {
                        this.startAutoSlide();
                    });
                }

            } catch (error) {
                console.error('Error fetching businesses:', error);
                if (reset) {
                    this.businesses = [];
                }
            } finally {
                this.loading = false;
                this.loadingMore = false;
            }
        },

        // ==================== SLIDER METHODS ====================

        startAutoSlide() {
            this.stopAutoSlide();

            if (this.businesses.length === 0) return;

            this.slideTimer = setInterval(() => {
                if (this.isPaused) return;
                this.nextSlide();
            }, this.slideInterval);

            console.log(`🎬 Auto-slide started (every ${this.slideInterval / 1000}s, ${this.cardsVisible} cards visible)`);
        },

        stopAutoSlide() {
            if (this.slideTimer) {
                clearInterval(this.slideTimer);
                this.slideTimer = null;
            }
        },

        nextSlide() {
            // Load more if near end of loaded cards
            if (
                this.currentSlide >= this.businesses.length - this.loadMoreThreshold &&
                this.hasMoreData &&
                !this.loadingMore
            ) {
                this.loadMore();
            }

            // Restart if reached end
            if (this.currentSlide >= this.maxSlide) {
                this.restart();
                return;
            }

            this.currentSlide = Math.min(
                this.currentSlide + this.cardsToSlide,
                this.maxSlide
            );

            this.updateOffset();
        },

        prevSlide() {
            if (this.currentSlide <= 0) {
                this.currentSlide = this.maxSlide;
            } else {
                this.currentSlide = Math.max(0, this.currentSlide - this.cardsToSlide);
            }

            this.updateOffset();
        },

        updateOffset() {
            // Each slide step = card width + gap
            const stepWidth = this.calculatedCardWidth + this.gap;
            this.offset = this.currentSlide * stepWidth;

            // Don't scroll past loaded cards
            const maxOffset = Math.max(
                0,
                (this.businesses.length - this.cardsVisible) * stepWidth
            );

            if (this.offset > maxOffset) {
                this.offset = maxOffset;
            }
        },

        restart() {
            console.log('🔄 Restarting slider from beginning');

            this.animate = false;
            this.currentSlide = 0;
            this.offset = 0;

            this.$nextTick(() => {
                setTimeout(() => {
                    this.animate = true;
                }, 50);
            });
        },

        togglePause() {
            this.isPaused = !this.isPaused;
            console.log(`⏸️ Auto-slide ${this.isPaused ? 'paused' : 'resumed'}`);
        },

        // ==================== DIMENSION METHODS ====================

        measureContainer() {
            const container = this.$refs.sliderContainer;
            if (container) {
                this.containerWidth = container.offsetWidth;
            }
        },

        handleResize() {
            this.measureContainer();
            this.$nextTick(() => {
                this.updateOffset();
            });
        },

        // ==================== EXISTING METHODS ====================

        debouncedSearch() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.resetAndFetch();
            }, 500);
        },

        resetAndFetch() {
            this.stopAutoSlide();
            this.restart();
            this.fetchBusinesses(true);
        },

        async loadMore() {
            if (!this.hasMoreData || this.loadingMore) return;
            if (this.businesses.length >= this.totalLimit) {
                this.hasMoreData = false;
                return;
            }

            this.currentPage++;
            await this.fetchBusinesses(false);
        },

        updateUrl(params) {
            const url = new URL(window.location);
            Object.keys(params).forEach(key => {
                if (params[key] && key !== 'page') {
                    url.searchParams.set(key, params[key]);
                } else {
                    url.searchParams.delete(key);
                }
            });
            window.history.pushState({}, '', url);
        },

        truncate(text, maxLength = 100) {
            if (!text || typeof text !== 'string') return '';
            if (text.length <= maxLength) return text;

            const truncated = text.substr(0, maxLength);
            const lastSpace = truncated.lastIndexOf(' ');

            if (lastSpace > 0 && lastSpace > maxLength - 20) {
                return truncated.substr(0, lastSpace) + '...';
            }

            return truncated + '...';
        },

        handleImageError(event) {
            event.target.src = 'https://listee.dreamstechnologies.com/vue/assets/listgrid-1-C35U96GW.jpg';
        },

        resetFilters() {
            this.search = '';
            this.sortBy = 'created_at';
            this.sortOrder = 'desc';
            this.resetAndFetch();
        },

        resetScrollPosition() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    },

    watch: {
        // Update offset when businesses load
        'businesses.length'() {
            this.$nextTick(() => {
                this.updateOffset();
            });
        }
    }
};
</script>

<style scoped>
/* ==================== EXISTING STYLES ==================== */

.spinner-border {
    width: 3rem;
    height: 3rem;
}

.spinner-border-sm {
    width: 1.5rem;
    height: 1.5rem;
}

.empty-state {
    padding: 3rem;
    background: #f8f9fa;
    border-radius: 10px;
}

.blog-img img {
    width: 100%;
    height: 200px;
    object-fit: cover;
}

.banner-placeholder {
    width: 100%;
    height: 100%;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgb(135, 212, 251);
}

.placeholder-content {
    text-align: center;
    padding: 20px;
}

.placeholder-content i {
    font-size: 3rem;
    margin-bottom: 10px;
    opacity: 0.8;
}

/* ==================== SLIDER STYLES ==================== */

.business-slider-wrapper {
    position: relative;
    width: 100%;
    overflow: hidden;
    padding: 10px 0;
}

.slider-status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.status-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.status-info .badge {
    font-size: 13px;
    padding: 6px 12px;
}

.status-controls {
    display: flex;
    gap: 8px;
}

/* ==================== SLIDER CONTAINER ==================== */

.business-slider-container {
    width: 100%;
    overflow: hidden;
    position: relative;
    padding: 15px 0;
}

.business-slider-track {
    display: flex;
    transition: transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    will-change: transform;
    /* gap handled via inline style */
}

.business-slider-track.no-transition {
    transition: none;
}

.business-slide-item {
    flex-shrink: 0;
    /* width set via inline style */
    box-sizing: border-box;
}

.business-slide-item .card {
    height: 100%;
    margin-bottom: 0;
}

/* ==================== SLIDER CONTROLS ==================== */

.slider-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
}

.slider-nav-btn {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid #ddd;
    background: white;
    color: #333;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}

.slider-nav-btn:hover:not(:disabled) {
    background: #0d6efd;
    color: white;
    border-color: #0d6efd;
    transform: scale(1.05);
}

.slider-nav-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.restart-btn {
    background: #f8f9fa;
}

.restart-btn:hover {
    background: #198754;
    border-color: #198754;
}

/* ==================== PROGRESS BAR ==================== */

.slider-progress {
    padding: 0 20px;
}

.progress-track {
    height: 6px;
    background: #eee;
    border-radius: 3px;
    overflow: hidden;
}

.progress-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #0d6efd, #198754);
    transition: width 0.5s ease;
    border-radius: 3px;
}

.progress-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 6px;
}

/* ==================== LOADING SLIDE ==================== */

.loading-slide-item {
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading-card {
    width: 100%;
    height: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
    border-radius: 10px;
    border: 2px dashed #ddd;
}

/* ==================== RESPONSIVE ==================== */

@media (max-width: 992px) {
    .slider-nav-btn {
        width: 40px;
        height: 40px;
        font-size: 14px;
    }
}

@media (max-width: 768px) {
    .slider-nav-btn {
        width: 38px;
        height: 38px;
        font-size: 13px;
    }

    .slider-status-bar {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
    }
}

/* ==================== ANIMATIONS ==================== */

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}

.card {
    animation: fadeIn 0.5s ease-out;
}
</style>